/**
 * ShareWithProviderDialog — a single-use 7-digit code + QR (healthRecordsShareService) that
 * hands a provider the patient's full MasterChart. Opened from MasterChart, and from a
 * completed CareStart session, which passes its results: then the token also references the
 * persisted session (users/{uid}/careStartSessions), so the provider receives its likely
 * conditions, care level and suggested tests alongside the record. Sharing is all-or-nothing
 * (no per-section picker). Signed-in patients only.
 */

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box,
  CircularProgress,
  Stack,
  Typography,
  IconButton,
  Alert,
  Button,
  Link,
  Snackbar,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";
import { QRCodeSVG } from "qrcode.react";
import { Trans, useTranslation } from "react-i18next";
import { format, isPast } from "date-fns";
import { AuthContext } from "context/Auth";
import { dateLocale } from "i18n";
import {
  SHAREABLE_SECTIONS,
  createShareToken,
  revokeShareToken,
  buildQrPayload,
  formatManualCode,
  getTokenValidityLabel,
} from "services/healthRecordsShareService";
import { persistCareStartSession } from "features/careStart/careStartSessionService";

// Sharing is all-or-nothing: every token includes the full record. The client wants a single,
// simple action rather than per-section toggles.
const ALL_SECTIONS = SHAREABLE_SECTIONS.reduce((acc, s) => {
  acc[s.id] = true;
  return acc;
}, {});

function ShareWithProviderDialog({
  open,
  onClose,
  ddxResult,
  triageResult,
  finalTranscript = "",
  patientAge = null,
}) {
  const { user, userData, medications } = useContext(AuthContext);
  const { t } = useTranslation("careStart");
  // Opened from CareStart when its results come along; from MasterChart otherwise.
  const forCareStart = triageResult != null || ddxResult != null;

  const healthRecords = userData?.healthRecords ?? {};
  const sectionAvailable = useMemo(
    () => ({
      demographics: !!(healthRecords.dateOfBirth || healthRecords.sex),
      conditions: (healthRecords.pmh?.length ?? 0) > 0,
      surgical: (healthRecords.psh?.length ?? 0) > 0,
      medications: (medications?.length ?? 0) > 0,
      allergies: (healthRecords.allergies?.length ?? 0) > 0,
      other: healthRecords.other
        ? Object.values(healthRecords.other).some((v) => v && String(v).trim() !== "")
        : false,
    }),
    [healthRecords, medications]
  );

  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [share, setShare] = useState(null);
  const [copied, setCopied] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [revoked, setRevoked] = useState(false);


  const expired = !!share && isPast(new Date(share.expiresAtMs));

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    setError(null);
    // Regenerating replaces the old code: revoke the previous token so it can't still be
    // redeemed during its (now multi-day) validity window.
    const previousTokenId = share?.tokenId;
    try {
      const sessionId = forCareStart
        ? await persistCareStartSession(user?.uid, {
            triage: triageResult,
            ddxResult,
            finalTranscript,
            patientAge,
          })
        : null;
      const result = await createShareToken({
        user,
        allowedSections: ALL_SECTIONS,
        careStartSessionId: sessionId,
      });
      setShare(result);
      setRevoked(false);
      if (previousTokenId) {
        // Best-effort; the old code also expires on its own if this fails.
        revokeShareToken(previousTokenId).catch(() => {});
      }
    } catch (err) {
      setError(err.message || t("share.generateError"));
    } finally {
      setGenerating(false);
    }
  }, [user, forCareStart, triageResult, ddxResult, finalTranscript, patientAge, share?.tokenId, t]);

  const handleRevoke = useCallback(async () => {
    if (!share?.tokenId) return;
    setRevoking(true);
    setError(null);
    try {
      await revokeShareToken(share.tokenId);
      setRevoked(true);
    } catch (err) {
      setError(err.message || t("share.revokeError"));
    } finally {
      setRevoking(false);
    }
  }, [share?.tokenId, t]);

  const handleCopy = useCallback(() => {
    if (!share?.manualCode || !navigator.clipboard) return;
    navigator.clipboard.writeText(formatManualCode(share.manualCode));
    setCopied(true);
  }, [share]);

  const qrValue = share ? buildQrPayload({ manualCode: share.manualCode }) : "";

  // Record sections that actually have data, for the "you will share" summary.
  const sharedLabels = SHAREABLE_SECTIONS.filter((s) => sectionAvailable[s.id]).map(
    (s) => t(`share.sections.${s.id}`, { defaultValue: s.label })
  );
  const hasAnyData = sharedLabels.length > 0;
  const canShare = hasAnyData || forCareStart;

  // Opening the dialog is the decision to share, so the code is made straight away
  // rather than behind a second "Generate" click; "revoke this code" undoes an
  // accidental open. It waits for the record when opened before it has loaded
  // (the menu's /masterchart/share on a fresh page), and the ref keeps it to one
  // code per opening (StrictMode runs effects twice in development).
  const autoStarted = useRef(false);
  useEffect(() => {
    if (!open) {
      autoStarted.current = false;
      setShare(null);
      setError(null);
      setRevoking(false);
      setRevoked(false);
      return;
    }
    if (autoStarted.current || !canShare) return;
    autoStarted.current = true;
    handleGenerate();
  }, [open, canShare]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t("share.title")}</DialogTitle>

      <DialogContent>
        {!share ? (
          generating ? (
            <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <DialogContentText>
              {t("share.nothingToShare")}
            </DialogContentText>
          )
        ) : (
          <Stack spacing={2} alignItems="center">
            <DialogContentText sx={{ alignSelf: "stretch" }}>
              {t(forCareStart ? "share.scanIntroCareStart" : "share.scanIntroMasterChart", {
                validity: getTokenValidityLabel(),
              })}
            </DialogContentText>
            {hasAnyData && (
              <Typography variant="body2" sx={{ alignSelf: "stretch" }}>
                {t("share.sharingLead")}{" "}
                {sharedLabels.map((label, i) => (
                  <React.Fragment key={label}>
                    {i > 0 &&
                      (i === sharedLabels.length - 1 ? t("share.listAnd") : t("share.listComma"))}
                    <Box component="span" fontWeight={600}>
                      {label}
                    </Box>
                  </React.Fragment>
                ))}
                {t("share.listEnd")}
              </Typography>
            )}
            {error && (
              <Alert severity="error" onClose={() => setError(null)} sx={{ width: "100%" }}>
                {error}
              </Alert>
            )}
            <Box
              sx={{
                p: 2,
                lineHeight: 0,
                bgcolor: "common.white",
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <QRCodeSVG value={qrValue} size={220} level="H" />
            </Box>

            <Box sx={{ textAlign: "center" }}>
              <Typography variant="overline" color="text.secondary">
                {t("share.code")}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Typography variant="h4" fontWeight={700} letterSpacing={2}>
                  {formatManualCode(share.manualCode)}
                </Typography>
                <IconButton onClick={handleCopy} aria-label={t("share.copyCode")}>
                  <ContentCopyRounded fontSize="small" />
                </IconButton>
              </Stack>
              {revoked ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t("share.revoked")}
                </Typography>
              ) : expired ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t("share.expired")}
                </Typography>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t("share.validUntil", {
                      date: format(new Date(share.expiresAtMs), "PP", { locale: dateLocale() }),
                      time: format(new Date(share.expiresAtMs), "p", { locale: dateLocale() }),
                    })}
                  </Typography>
                  {revoking ? (
                    <Box sx={{ mt: 0.5, display: "flex", justifyContent: "center" }}>
                      <CircularProgress size={16} />
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>
                      <Trans
                        ns="careStart"
                        i18nKey="share.revokeEarly"
                        components={{
                          1: (
                            <Link
                              component="button"
                              type="button"
                              variant="inherit"
                              color="inherit"
                              underline="always"
                              onClick={handleRevoke}
                              sx={{ verticalAlign: "baseline" }}
                            />
                          ),
                        }}
                      />
                    </Typography>
                  )}
                </>
              )}
            </Box>

            {(expired || revoked) && (
              <Alert severity="warning" sx={{ width: "100%" }}>
                {revoked
                  ? t("share.revokedAlert")
                  : t("share.expiredAlert")}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        {!share ? (
          <>
            <Button onClick={onClose}>{canShare ? t("share.cancel") : t("share.close")}</Button>
            {error && (
              <LoadingButton variant="contained" onClick={handleGenerate} loading={generating}>
                {t("share.tryAgain")}
              </LoadingButton>
            )}
          </>
        ) : (
          <>
            <LoadingButton
              variant={expired || revoked ? "contained" : "outlined"}
              onClick={handleGenerate}
              loading={generating}
            >
              {t("share.newCode")}
            </LoadingButton>
            <Button variant="contained" onClick={onClose}>
              {t("share.done")}
            </Button>
          </>
        )}
      </DialogActions>

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message={t("share.copied")}
      />
    </Dialog>
  );
}

ShareWithProviderDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  ddxResult: PropTypes.object,
  triageResult: PropTypes.object,
  finalTranscript: PropTypes.string,
  patientAge: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default ShareWithProviderDialog;
